import React, { useEffect, useState } from "react";
import { Box, Typography, Card, Chip, Button, Tooltip, CircularProgress } from "@mui/material";
import {
  WorkspacePremium,
  CheckCircle,
  AccountBalance,
  UploadFile,
  AutoAwesome,
  LocalOffer,
  SupportAgent,
  NewReleases,
  Star,
} from "@mui/icons-material";
import PageHeader from "../../components/common/PageHeader";
import systemSettingsService from "../../services/systemSettingsService";

const TEAL = "#00a6b2";
const NAVY = "#081b33";
const MUTED = "#8a94a6";

// Keys match the `icon` string an admin picks on AdminUpgradePage.jsx
// (stored in SystemSettings.premiumPageContent.features[].icon) — "star" is
// the fallback for anything not in this map, so old/unrecognized data never
// renders blank.
export const PREMIUM_FEATURE_ICONS = {
  accountBalance: AccountBalance,
  uploadFile: UploadFile,
  autoAwesome: AutoAwesome,
  localOffer: LocalOffer,
  supportAgent: SupportAgent,
  newReleases: NewReleases,
  star: Star,
};

// Nothing here is wired to billing yet — pricing isn't finalized and
// there's no signup/checkout flow built. This is a preview page (same
// "coming soon" spirit as AnalyzeWithAiButton) so customers can see what
// Premium will unlock and roughly what it'll cost, before it's real. Its
// heading/pricing text and feature list are admin-editable from
// AdminUpgradePage.jsx (SystemSettings.premiumPageContent) rather than
// hardcoded, so marketing copy can change without a deploy.
const CustomerUpgrade = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    systemSettingsService
      .getPremiumPageContent()
      .then(({ data }) => {
        if (active) setContent(data.data.premiumPageContent);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  const features = content?.features?.length ? content.features : [];

  return (
    <Box>
      <PageHeader
        title="Upgrade to Premium"
        subtitle="Unlock more ways to manage your shipments."
      />

      <Card
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          mb: 3,
          textAlign: "center",
          background: `linear-gradient(135deg, ${NAVY} 0%, #0d3a63 100%)`,
          color: "#fff",
        }}
      >
        <WorkspacePremium sx={{ fontSize: 40, color: TEAL, mb: 1 }} />
        <Typography variant="h5" fontWeight={700}>
          {content?.heading || "Waypoint Premium"}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5, mb: 2 }}>
          {content?.priceText || "Starting from around $10–15/month — final pricing coming soon."}
        </Typography>
        <Tooltip title="Premium accounts aren't open yet — check back soon!" arrow enterTouchDelay={0}>
          <span>
            <Button
              variant="contained"
              disabled
              sx={{ bgcolor: TEAL, "&.Mui-disabled": { bgcolor: "rgba(0,166,178,0.4)", color: "#fff" } }}
            >
              {content?.ctaText || "Coming Soon"}
            </Button>
          </span>
        </Tooltip>
      </Card>

      {features.length > 0 && (
        <>
          <Typography variant="overline" sx={{ color: MUTED, fontWeight: 700, display: "block", mb: 1.5 }}>
            What's included
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            {features.map((feature, idx) => {
              const Icon = PREMIUM_FEATURE_ICONS[feature.icon] || Star;
              return (
                <Card key={idx} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "rgba(0,0,0,0.08)" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        bgcolor: "rgba(0,166,178,0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 18, color: TEAL }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "text.primary" }}>{feature.title}</Typography>
                    {feature.comingSoon && (
                      <Chip
                        label="Soon"
                        size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "rgba(0,166,178,0.12)", color: TEAL }}
                      />
                    )}
                  </Box>
                  {feature.description && (
                    <Typography variant="body2" sx={{ color: MUTED }}>
                      {feature.description}
                    </Typography>
                  )}
                </Card>
              );
            })}
          </Box>
        </>
      )}

      <Card sx={{ p: 3, borderRadius: 3, mt: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
        <CheckCircle sx={{ color: TEAL, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: MUTED }}>
          Already have a feature from this list enabled on your account (e.g. Multi-TIN or Bulk Add)? That was
          turned on for you directly — you don't need to wait for Premium to launch to keep using it.
        </Typography>
      </Card>
    </Box>
  );
};

export default CustomerUpgrade;
